import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Note: In a real implementation, you would need to convert these to PDF-compatible graphics
// These are placeholder descriptions for the flow charts
const FlowChart = ({ title, steps }: { title: string; steps: string[] }) => (
  <View style={styles.flowChart}>
    <Text style={styles.flowChartTitle}>{title}</Text>
    <View style={styles.flowChartSteps}>
      {steps.map((step, i) => (
        <View key={i} style={styles.flowChartStep}>
          <View style={styles.flowChartNumber}>
            <Text style={styles.flowChartNumberText}>{i + 1}</Text>
          </View>
          <Text style={styles.flowChartStepText}>{step}</Text>
        </View>
      ))}
    </View>
  </View>
);

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF',
    color: '#1F2937',
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    fontSize: 24,
    fontWeight: 'extrabold',
    marginBottom: 5,
    color: '#111827',
  },
  subtitle: {
    fontSize: 14,
    color: '#4B5563',
    fontStyle: 'italic',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#111827',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: 5,
  },
  listItem: {
    marginBottom: 8,
    marginLeft: 15,
    fontSize: 12,
  },
  tocItem: {
    marginBottom: 10,
    color: '#2563EB',
    textDecoration: 'none',
    fontSize: 12,
  },
  chapterTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 12,
    color: '#111827',
    paddingBottom: 5,
    borderBottom: '1px solid #E5E7EB',
  },
  subSection: {
    marginLeft: 10,
    marginBottom: 15,
  },
  subSectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 8,
    color: '#1F2937',
  },
  note: {
    fontSize: 10,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 10,
    color: '#9CA3AF',
    borderTop: '1px solid #E5E7EB',
    paddingTop: 10,
  },
  flowChart: {
    marginVertical: 15,
    padding: 15,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    borderLeft: '4px solid #3B82F6',
  },
  flowChartTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1F2937',
  },
  flowChartSteps: {
    marginLeft: 10,
  },
  flowChartStep: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  flowChartNumber: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
    flexShrink: 0,
  },
  flowChartNumberText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  flowChartStepText: {
    fontSize: 11,
    lineHeight: 1.4,
    flex: 1,
  },
  highlight: {
    backgroundColor: '#FEF3C7',
    padding: 10,
    borderRadius: 4,
    marginVertical: 10,
    borderLeft: '3px solid #F59E0B',
  },
  quote: {
    fontStyle: 'italic',
    color: '#4B5563',
    borderLeft: '3px solid #D1D5DB',
    paddingLeft: 10,
    margin: '10px 0',
  },
});

const FinancialPlanningHandbook = () => {
  const financialPlanningSteps = [
    'Assess your current financial situation',
    'Define clear financial goals',
    'Analyze financial options and strategies',
    'Develop a comprehensive financial plan',
    'Implement the financial plan',
    'Monitor and adjust the plan regularly'
  ];

  const debtManagementSteps = [
    'List all debts with balances and interest rates',
    'Choose a strategy (Snowball or Avalanche)',
    'Pay minimums on all debts',
    'Apply extra payments to target debt',
    'Repeat until all debts are paid off'
  ];

  const investmentStrategySteps = [
    'Determine risk tolerance and time horizon',
    'Set investment goals',
    'Choose asset allocation strategy',
    'Select specific investments',
    'Implement and automate investments',
    'Review and rebalance portfolio regularly'
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <View style={styles.header}>
            <Text style={styles.title}>Financial Planning Handbook</Text>
            <Text style={styles.subtitle}>Your Comprehensive Guide to Financial Freedom</Text>
          </View>

          {/* Table of Contents */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Table of Contents</Text>
            {[
              '1. Overview & Planning Framework',
              '2. Budgeting & Cash-Flow Management',
              '3. Emergency Fund Strategy',
              '4. Debt Management Techniques',
              '5. Savings & Investment Strategy',
              '6. Risk Management & Insurance',
              '7. Retirement, Tax & Estate Planning',
              '8. Implementation & Monitoring',
              '9. Common Pitfalls to Avoid',
              '10. One-Page Financial Plan Template',
              '11. Tools & Resources'
            ].map((item, index) => (
              <Text key={index} style={styles.tocItem}>
                {item}
              </Text>
            ))}
          </View>

          {/* 1. Overview & Planning Framework */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>1. Overview & Planning Framework</Text>
            
            <View style={styles.subSection}>
              <Text style={styles.sectionTitle}>Definition & Purpose</Text>
              <Text style={styles.listItem}>• Financial planning means mapping your current finances, setting goals, and outlining steps to achieve them</Text>
              <Text style={styles.listItem}>• Covers cash flow, investing, insurance, taxes, retirement, and estate planning</Text>
            </View>
            
            <View style={styles.subSection}>
              <FlowChart 
            title="Financial Planning Process"
            steps={financialPlanningSteps}
          />
        </View>
      </View>

      {/* 2. Budgeting & Cash-Flow Management */}
      <View style={styles.section}>
        <Text style={styles.chapterTitle}>2. Budgeting & Cash-Flow Management</Text>
        <Text style={styles.sectionTitle}>Core Principle</Text>
        <Text style={styles.listItem}>• Spend less than you earn</Text>
        
        <Text style={styles.sectionTitle}>Popular Methods</Text>
        <Text style={styles.listItem}>• 50/30/20 rule: 50% needs / 30% wants / 20% savings/debt</Text>
        <Text style={styles.listItem}>• Zero-based budgeting</Text>
        <Text style={styles.listItem}>• Envelope system</Text>
        
        <Text style={styles.sectionTitle}>Tracking</Text>
        <Text style={styles.listItem}>• Use budgeting tools, apps, spreadsheets</Text>
        <Text style={styles.listItem}>• Review monthly</Text>
      </View>

      {/* 3. Emergency Fund Strategy */}
      <View style={styles.section}>
        <Text style={styles.chapterTitle}>3. Emergency Fund Strategy</Text>
        <Text style={styles.listItem}>• Maintain 3–6 months of living expenses in a liquid fund</Text>
        <Text style={styles.listItem}>• Covers emergencies like illness or repairs</Text>
        <Text style={styles.listItem}>• Replenish immediately after use</Text>
      </View>

      {/* 4. Debt Management Techniques */}
      <View style={styles.section}>
        <Text style={styles.chapterTitle}>4. Debt Management Techniques</Text>
        
        <View style={styles.subSection}>
          <Text style={styles.sectionTitle}>Debt Payoff Strategies</Text>
          <View style={{flexDirection: 'row', marginBottom: 10}}>
            <View style={{flex: 1, marginRight: 10, padding: 10, backgroundColor: '#F0F9FF', borderRadius: 6}}>
              <Text style={{fontWeight: 'bold', color: '#0369A1', marginBottom: 5}}>Snowball Method</Text>
              <Text style={{fontSize: 10}}>1. Pay minimums on all debts</Text>
              <Text style={{fontSize: 10}}>2. Put extra money toward smallest debt</Text>
              <Text style={{fontSize: 10}}>3. Repeat until all debts are paid</Text>
              <Text style={{fontSize: 10, fontStyle: 'italic', marginTop: 5}}>Best for: Quick wins and motivation</Text>
            </View>
            <View style={{flex: 1, padding: 10, backgroundColor: '#F0FDF4', borderRadius: 6}}>
              <Text style={{fontWeight: 'bold', color: '#047857', marginBottom: 5}}>Avalanche Method</Text>
              <Text style={{fontSize: 10}}>1. Pay minimums on all debts</Text>
              <Text style={{fontSize: 10}}>2. Put extra money toward highest interest debt</Text>
              <Text style={{fontSize: 10}}>3. Repeat until all debts are paid</Text>
              <Text style={{fontSize: 10, fontStyle: 'italic', marginTop: 5}}>Best for: Saving on interest</Text>
            </View>
          </View>
          
          <FlowChart 
            title="Debt Payoff Process"
            steps={debtManagementSteps}
          />
          
          <Text style={{...styles.sectionTitle, marginTop: 15}}>Other Strategies</Text>
          <Text style={styles.listItem}>• Consolidation: Combine multiple debts into a single loan with lower interest</Text>
          <Text style={styles.listItem}>• Balance Transfer: Move high-interest credit card debt to a 0% APR card</Text>
          <Text style={styles.listItem}>• Priority Rule ("Rule of 7"): Pay off debt {'>'}7% interest before investing</Text>
        </View>
      </View>

      {/* 5. Savings & Investment Strategy */}
      <View style={styles.section}>
        <Text style={styles.chapterTitle}>5. Savings & Investment Strategy</Text>
            
            <View style={styles.subSection}>
              <Text style={styles.sectionTitle}>Investment Pyramid</Text>
              <View style={{marginVertical: 10, border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden'}}>
                <View style={{backgroundColor: '#1D4ED8', padding: 8}}>
                  <Text style={{color: 'white', textAlign: 'center', fontSize: 10, fontWeight: 'bold'}}>Wealth Building (Stocks, Real Estate, Business)</Text>
                </View>
                <View style={{backgroundColor: '#3B82F6', padding: 8, borderTop: '1px solid #E5E7EB'}}>
                  <Text style={{color: 'white', textAlign: 'center', fontSize: 10, fontWeight: 'bold'}}>Growth (ETFs, Mutual Funds, Index Funds)</Text>
                </View>
                <View style={{backgroundColor: '#60A5FA', padding: 8, borderTop: '1px solid #E5E7EB'}}>
                  <Text style={{color: 'white', textAlign: 'center', fontSize: 10, fontWeight: 'bold'}}>Income (Bonds, Dividend Stocks, REITs)</Text>
                </View>
                <View style={{backgroundColor: '#93C5FD', padding: 8, borderTop: '1px solid #E5E7EB'}}>
                  <Text style={{color: 'white', textAlign: 'center', fontSize: 10, fontWeight: 'bold'}}>Emergency Fund (Savings, Money Market, CDs)</Text>
                </View>
              </View>
              
              <FlowChart 
                title="Investment Strategy Process"
                steps={investmentStrategySteps}
              />
              
              <Text style={styles.sectionTitle}>Key Principles</Text>
              <View style={{marginLeft: 10}}>
                <View style={{flexDirection: 'row', marginBottom: 5}}>
                  <Text style={{fontWeight: 'bold', width: 120}}>Diversification:</Text>
                  <Text style={{fontSize: 11}}>Spread investments across different asset classes to reduce risk</Text>
                </View>
                <View style={{flexDirection: 'row', marginBottom: 5}}>
                  <Text style={{fontWeight: 'bold', width: 120}}>Dollar-Cost Averaging:</Text>
                  <Text style={{fontSize: 11}}>Invest fixed amounts regularly regardless of market conditions</Text>
                </View>
                <View style={{flexDirection: 'row', marginBottom: 5}}>
                  <Text style={{fontWeight: 'bold', width: 120}}>Asset Allocation:</Text>
                  <Text style={{fontSize: 11}}>Distribute investments based on risk tolerance and time horizon</Text>
                </View>
                <View style={{flexDirection: 'row', marginBottom: 5}}>
                  <Text style={{fontWeight: 'bold', width: 120}}>Long-term Focus:</Text>
                  <Text style={{fontSize: 11}}>Stay invested through market fluctuations for compound growth</Text>
                </View>
              </View>
            </View>
          </View>

          {/* 7. Risk Management & Insurance */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>7. Risk Management & Insurance</Text>
            <Text style={styles.listItem}>• Include: health, auto, home/renters, life insurance</Text>
            <Text style={styles.listItem}>• Review coverage regularly as life situations change</Text>
          </View>

          {/* 8. Retirement, Tax & Estate Planning */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>8. Retirement, Tax & Estate Planning</Text>
            <Text style={styles.sectionTitle}>Retirement</Text>
            <Text style={styles.listItem}>• Start early, maximize employer contributions</Text>
            
            <Text style={styles.sectionTitle}>Tax Planning</Text>
            <Text style={styles.listItem}>• Use tax-advantaged accounts</Text>
            <Text style={styles.listItem}>• Coordinate with financial and investment decisions</Text>
            
            <Text style={styles.sectionTitle}>Estate Planning</Text>
            <Text style={styles.listItem}>• Prepare wills, trusts, and legacy documents</Text>
          </View>

          {/* 9. Implementation & Monitoring */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>9. Implementation & Ongoing Monitoring</Text>
            <Text style={styles.listItem}>• Present strategies clearly, document assumptions</Text>
            <Text style={styles.listItem}>• Review progress at least annually</Text>
            <Text style={styles.listItem}>• Update goals, budget, portfolio, insurance, estate plans</Text>
          </View>

          {/* 10. Common Pitfalls to Avoid */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>10. Common Pitfalls to Avoid</Text>
            <Text style={styles.listItem}>• No clear goals</Text>
            <Text style={styles.listItem}>• Insufficient emergency fund</Text>
            <Text style={styles.listItem}>• High-interest debt neglected</Text>
            <Text style={styles.listItem}>• Lack of investment diversification</Text>
            <Text style={styles.listItem}>• Overlooking insurance and estate needs</Text>
          </View>

          {/* 11. One-Page Financial Plan Template */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>11. One-Page Financial Plan Template</Text>
            <Text style={styles.sectionTitle}>Sections:</Text>
            <Text style={styles.listItem}>• Net Worth</Text>
            <Text style={styles.listItem}>• SMART Goals (Specific, Measurable, Achievable, Relevant, Time-bound)</Text>
            <Text style={styles.listItem}>• Monthly Budget Summary</Text>
            <Text style={styles.listItem}>• Debt Strategy</Text>
            <Text style={styles.listItem}>• Investment Allocation</Text>
          </View>

          {/* 12. Tools & Resources */}
          <View style={styles.section}>
            <Text style={styles.chapterTitle}>12. Tools & Resources</Text>
            <Text style={styles.sectionTitle}>Guides/PDFs</Text>
            <Text style={styles.listItem}>• CFP Board's 7-step guide</Text>
            
            <Text style={styles.sectionTitle}>Apps</Text>
            <Text style={styles.listItem}>• Budget trackers</Text>
            <Text style={styles.listItem}>• Investment calculators</Text>
            <Text style={styles.listItem}>• Retirement planners</Text>
            
            <Text style={styles.sectionTitle}>Podcasts</Text>
            <Text style={styles.listItem}>• The Ramsey Show</Text>
            <Text style={styles.listItem}>• Frugal Friends Podcast</Text>
            <Text style={styles.listItem}>• Money with Katie</Text>
            
            <Text style={styles.sectionTitle}>Books</Text>
            <Text style={styles.listItem}>• The Index Card</Text>
            <Text style={styles.listItem}>• The Intelligent Investor</Text>
            <Text style={styles.listItem}>• The Psychology of Money</Text>
          </View>

          {/* Final Takeaways */}
          <View style={[styles.section, styles.highlight]}>
            <Text style={styles.sectionTitle}>Key Takeaways</Text>
            <Text style={styles.listItem}>• Build your plan around clear goals, disciplined budgeting, and smart debt and investment strategies</Text>
            <Text style={styles.listItem}>• Regularly review and adapt—your financial plan is a living roadmap</Text>
            <Text style={styles.listItem}>• Use professional advice when needed—but understand the fundamentals yourself</Text>
          </View>
          
          <View style={styles.footer}>
            <Text>© {new Date().getFullYear()} White Pearl Finance - Financial Planning Handbook</Text>
            <Text style={{fontSize: 8, marginTop: 5}}>Last updated: July 2025</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default FinancialPlanningHandbook;
